/**
 * Task list component - fetches and displays contractor tasks.
 * Shows SO numbers only (no customer names per security requirements).
 */

import { useState } from 'react';
import { TaskCard } from './TaskCard';
import { TaskListSkeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { useTasks } from '../hooks/useTasks';
import type { TaskStatus } from 'scheduler-types';

// Filter tabs for task status
const FILTER_TABS = [
  { label: 'Active', statuses: ['ready', 'in_progress'] as TaskStatus[] },
  { label: 'All', statuses: undefined },
  { label: 'Pending', statuses: ['pending', 'staged', 'partial_staged'] as TaskStatus[] },
  { label: 'Done', statuses: ['complete'] as TaskStatus[] },
] as const;

interface TaskListProps {
  onTaskSelect?: (taskId: string) => void;
}

export function TaskList({ onTaskSelect }: TaskListProps) {
  const [activeTab, setActiveTab] = useState(0);
  const activeFilter = FILTER_TABS[activeTab];

  const { tasks, total, isLoading, isError, error, refetch } = useTasks({
    status: activeFilter?.statuses as TaskStatus[] | undefined,
  });

  const handleTaskClick = (taskId: string) => {
    onTaskSelect?.(taskId);
  };

  return (
    <div className="flex flex-col min-h-0">
      {/* Filter tabs */}
      <div className="flex-none px-4 pt-2 pb-3">
        <div className="flex gap-1 p-1 bg-surface-raised rounded-lg">
          {FILTER_TABS.map((tab, index) => (
            <button
              key={tab.label}
              type="button"
              onClick={() => setActiveTab(index)}
              className={`
                flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors
                ${
                  activeTab === index
                    ? 'bg-surface-overlay text-ink'
                    : 'text-ink-muted hover:text-ink hover:bg-surface-overlay/50'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Task count */}
      {!isLoading && !isError && tasks.length > 0 && (
        <div className="flex-none px-4 pb-2">
          <p className="text-xs text-ink-subtle">
            {total} task{total !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Task list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {isLoading ? (
          <TaskListSkeleton count={4} />
        ) : isError ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : tasks.length === 0 ? (
          <EmptyState filter={activeFilter?.label ?? 'Active'} />
        ) : (
          <div className="space-y-3">
            {tasks.map((task, index) => (
              <div
                key={task.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <TaskCard task={task} onClick={() => handleTaskClick(task.id)} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
